/**
 * Dashboard Needs Attention — Compact alert bar
 *
 * Renders attention items as a horizontal scrollable row of compact chips.
 * Each chip shows count + label as a tappable link.
 * Returns null when empty — no placeholder.
 */

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  AlertTriangle,
  Info,
  ChevronRight,
} from "lucide-react";
import type { NeedsAttentionItem } from "@/hooks/useDashboardPulse";

const priorityConfig: Record<
  NeedsAttentionItem["priority"],
  { icon: typeof AlertCircle; bg: string; text: string; accent: string }
> = {
  high: {
    icon: AlertCircle,
    bg: "bg-red-50 dark:bg-red-950/40",
    text: "text-red-700 dark:text-red-400",
    accent: "text-red-600 dark:text-red-400",
  },
  medium: {
    icon: AlertTriangle,
    bg: "bg-amber-50 dark:bg-amber-950/40",
    text: "text-amber-700 dark:text-amber-400",
    accent: "text-amber-600 dark:text-amber-400",
  },
  low: {
    icon: Info,
    bg: "bg-blue-50 dark:bg-blue-950/40",
    text: "text-blue-700 dark:text-blue-400",
    accent: "text-blue-600 dark:text-blue-400",
  },
};

interface DashboardPendingTasksProps {
  tasks: NeedsAttentionItem[];
  title: string;
}

export function DashboardPendingTasks({ tasks, title }: DashboardPendingTasksProps) {
  if (tasks.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.25 }}
      role="region"
      aria-label="Needs attention"
    >
      <h2 className="text-sm font-semibold text-foreground mb-3">
        {title}
      </h2>
      <div className="flex gap-3 overflow-x-auto scrollbar-none pb-1">
        {tasks.slice(0, 6).map((task) => {
          const config = priorityConfig[task.priority] ?? priorityConfig.medium;
          const Icon = config.icon;
          return (
            <Link
              key={task.id}
              to={task.href}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl flex-shrink-0",
                "border border-border/40",
                "transition-all hover:shadow-sm active:scale-[0.98]",
                config.bg,
                "group"
              )}
            >
              <Icon className={cn("w-4 h-4 flex-shrink-0", config.accent)} />
              <span className={cn("text-2xl font-semibold leading-none", config.text)}>
                {task.count}
              </span>
              <span className="text-sm text-foreground whitespace-nowrap">
                {task.title}
              </span>
              <ChevronRight className="w-4 h-4 text-muted-foreground/60 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
            </Link>
          );
        })}
      </div>
    </motion.div>
  );
}
